import { amorphicStatic } from '@haventech/amorphic';
import requestPromise from 'request-promise-native';

import { serverOnly } from '../../helpers';
import { Auth0RequestTypes, Auth0Response } from '../Auth0Client';

import { CustomerAgreementApiResponse, CustomerApiConfigs, SanderlingPolicyTypes } from './MMCustomerApiUtils';
import { CPLogger, Category } from '../../logger/CPLogger';
import { LoggerSingleton } from '../../logger/LoggerSingleton';

const DEFAULT_REQUEST_TIMEOUT: number = 1000 * 60 * 1;
const API_SUFFIX: string = '/v1/token';

export class MMCustomerApiClient {
  @serverOnly()
  static async getCustomerPolicies(user_amc_guid: string): Promise<CustomerAgreementApiResponse> {
    if (!user_amc_guid) {
      return null;
    }

    const token = await MMCustomerApiClient.acquireNewCustomerApiToken();
    const cfgs = amorphicStatic.config.nconf.get('mmCustomerApi');
    const uri = `${cfgs.domain}/${user_amc_guid}/agreements`;

    const options = {
      uri,
      method: 'GET',
      json: true,
      timeout: DEFAULT_REQUEST_TIMEOUT,
      auth: {
        bearer: token,
      },
      headers: {
        requestorId: cfgs.requestorId,
        ...LoggerSingleton.getInstance()?.getHeaderContextFromLocalStorage?.(),
      },
    };

    const loggerData = {
      module: 'MMCustomerApiClient',
      function: 'getCustomerPolicies',
      data: {
        amcGuid: user_amc_guid,
        uri,
      },
    };

    try {
      const response: CustomerAgreementApiResponse = await requestPromise(options);

      LoggerSingleton.getInstance().info(
        {
          ...loggerData,
          category: Category.request,
        },
        `Sent request to MM to get policy information of the customer`
      );

      return response;
    } catch (error) {
      LoggerSingleton.getInstance().error(
        {
          ...loggerData,
          category: Category.request,

          error: {
            name: error?.name,
            message: error?.message,
            stack: error?.stack,
            isHumanRelated: false,
          },
        },
        'Failed to get policy information of the customer from MM'
      );

      throw error;
    }
  }

  @serverOnly()
  public static getMMAuth0Configs(): CustomerApiConfigs {
    const client_secret: string = amorphicStatic.config.nconf.get('auth0APIClientSecret_MMCUSTOMERAPI');
    const apiDomain = amorphicStatic.config.nconf.get('AUTH0_API_DOMAIN_MMCUSTOMERAPI');
    const apiScope = amorphicStatic.config.nconf.get('auth0APIScope_MMCUSTOMERAPI');
    const client_id = amorphicStatic.config.nconf.get('AUTH0_API_CLIENT_ID_MMCUSTOMERAPI');

    return {
      client_secret,
      apiDomain,
      apiScope,
      client_id,
      grant_type: 'client_credentials',
    };
  }

  @serverOnly()
  public static async acquireNewCustomerApiToken(): Promise<string> {
    const configs: CustomerApiConfigs = this.getMMAuth0Configs();
    const loggerData = {
      module: 'MMCustomerApiClient',
      function: 'acquireNewCustomerApiToken',
    };

    const url = new URL(`${configs.apiDomain}${API_SUFFIX}`);

    const params = new URLSearchParams({
      grant_type: configs.grant_type,
      scope: configs.apiScope,
    });

    url.search = params.toString();

    const base64AuthHeader = Buffer.from(`${configs.client_id}:${configs.client_secret}`).toString('base64');

    const options = {
      uri: url,
      method: Auth0RequestTypes.POST,
      rejectUnauthorized: false,
      json: true,
      timeout: DEFAULT_REQUEST_TIMEOUT,
      headers: {
        'accept': 'application/json',
        'Authorization': `Basic ${base64AuthHeader}`,
        'content-type': 'application/x-www-form-urlencoded',
        ...LoggerSingleton.getInstance()?.getHeaderContextFromLocalStorage?.(),
      },
    };

    try {
      const response: Auth0Response = await requestPromise(options);

      LoggerSingleton.getInstance().info(
        {
          ...loggerData,
          category: Category.request,
        },
        'Successfully acquired new access token'
      );

      return response.access_token;
    } catch (error) {
      LoggerSingleton.getInstance().error(
        {
          ...loggerData,
          category: Category.request,

          error: {
            name: error?.name,
            message: error?.message,
            stack: error?.stack,
            isHumanRelated: false,
            code: error?.code,
          },
        },
        'There was an issue acquiring new API Token'
      );

      throw new Error(`Unable to acquire new API token`);
    }
  }
}
